import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Link } from '~/lib/react-router-dom';

import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { AlertColor, SelectChangeEvent } from '@mui/material';
import { DataGrid, GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import ColumnSelector from '~/components/Promo/PromoTester/ColumnSelector';

import { Voucher, getVoucherWarnings } from '~/services/PromoService';

import DateFormatterDetailed from '~/utils/dateFormatterDetails';

import DebugModal from '../DebugModal/DebugModal';

import {
  CASE_ID_FILTER_PRESET_OPTIONS,
  caseIdFilterOptionLabel,
  normalizeCaseIdFilterToken,
} from './caseIdFilterTokens';

type Props = {
  voucherName: string;
  setVoucherName: (v: string) => void;
  reportingTagOptions: Array<{ value: string; label: string }>;
  selectedReportingTags: Array<string>;
  setSelectedReportingTags: (v: Array<string>) => void;
  caseIdFilter: Array<string>;
  setCaseIdFilter: (v: Array<string>) => void;
  /** Shown on the switch; false while a case ID filter is applied. */
  effectiveOnlyActive: boolean;
  caseIdFilterActive: boolean;
  setOnlyActive: (v: boolean) => void;
  data: Array<Voucher>;
  isLoading: boolean;
  totalCount: number;
  pageNum: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  onConfirmDelete: (voucher: Voucher) => Promise<void>;
  isDeleting: boolean;
};

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: true,
  disableColumnMenu: true,
  flex: 0.5,
};

function getVoucherWarningSeverity(level?: string): AlertColor {
  if (level === 'error' || level === 'warning' || level === 'info' || level === 'success') {
    return level;
  }
  return 'warning';
}

function getColumns({
  onDelete,
  onOpenWarnings,
}: {
  onDelete: (row: Voucher) => void;
  onOpenWarnings: (row: Voucher) => void;
}) {
  const columns: Array<GridColDef<Voucher>> = [
    {
      field: 'voucherId',
      headerName: 'ID',
      ...sharedOpts,
      renderCell: (params) => params.row.voucherId || '',
    },
    {
      field: 'voucherName',
      headerName: 'Voucher Name',
      ...sharedOpts,
      renderCell: (params) => params.row.voucherName || '',
    },
    {
      field: 'voucherTitle',
      headerName: 'Voucher Title',
      ...sharedOpts,
      renderCell: (params) => params.row.voucherTitle || '',
    },
    {
      field: 'voucherExpiry',
      headerName: 'Voucher Expiry',
      ...sharedOpts,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.row.voucherUserExpiresInDays
            ? `${params.row.voucherUserExpiresInDays} days`
            : params.row.voucherUserExpiresAt
              ? DateFormatterDetailed({ date: params.row.voucherUserExpiresAt, highlightIfPast: true })
              : ''}
        </Typography>
      ),
    },
    {
      field: 'caseId',
      headerName: 'Case ID',
      ...sharedOpts,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.row.caseId || ''}
          {params.row.reason && (
            <Typography variant="body2" color="text.secondary">
              {params.row.reason || ''}
            </Typography>
          )}
        </Typography>
      ),
    },
    {
      field: 'parentVoucherId',
      headerName: 'Parent voucher',
      ...sharedOpts,
      valueGetter: (_value, row) => {
        const id = (row as Voucher & { clonedFromId?: string | null }).clonedFromId;
        return typeof id === 'string' ? id.trim() : '';
      },
      renderCell: (params) => {
        const id = params.value as string;
        if (!id) {
          return '';
        }
        return (
          <Button
            variant="text"
            size="small"
            component={Link}
            to={`/marketing/vouchers/${id}`}
            sx={{ p: 0, minWidth: 0 }}
          >
            {id}
          </Button>
        );
      },
    },
    {
      field: 'theme',
      headerName: 'Theme',
      ...sharedOpts,
      renderCell: (params) => params.row.theme || '',
    },
    {
      field: 'productType',
      headerName: 'Product Type',
      ...sharedOpts,
      renderCell: (params) => params.row.productType || '',
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      ...sharedOpts,
      renderCell: (params) => DateFormatterDetailed({ date: params.row.createdAt }),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      ...sharedOpts,
      renderCell: (params) => DateFormatterDetailed({ date: params.row.createdAt }),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      ...sharedOpts,
      hideable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button variant="outlined" size="small" component={Link} to={`/marketing/vouchers/${params.row.voucherId}`}>
            View
          </Button>
        </Stack>
      ),
    },
    {
      field: 'debug',
      headerName: 'Debug',
      ...sharedOpts,
      renderCell: (params) => {
        const warnings = getVoucherWarnings(params.row);
        const warningCount = warnings.length;
        const warningsLabel = `${warningCount} ${warningCount === 1 ? 'warning' : 'warnings'}`;
        return (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {warningCount === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {warningsLabel}
              </Typography>
            ) : (
              <Button variant="text" size="small" onClick={() => onOpenWarnings(params.row)}>
                {warningsLabel}
              </Button>
            )}
            <DebugModal type="generic" title="Debug" data={params.row} />
          </Stack>
        );
      },
    },
  ];
  return columns;
}

export default function VouchersTab({
  voucherName,
  setVoucherName,
  reportingTagOptions,
  selectedReportingTags,
  setSelectedReportingTags,
  caseIdFilter,
  setCaseIdFilter,
  effectiveOnlyActive,
  caseIdFilterActive,
  setOnlyActive,
  data,
  isLoading,
  totalCount,
  pageNum,
  pageSize,
  onPageChange,
  onConfirmDelete,
  isDeleting,
}: Props) {
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<Voucher | undefined>(undefined);
  const [warningsDialogVoucher, setWarningsDialogVoucher] = useState<Voucher | undefined>(undefined);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [voucherToClone, setVoucherToClone] = useState<Voucher | undefined>(undefined);
  /** Pending text in the Case ID Autocomplete (freeSolo values only commit on Enter unless we flush on blur). */
  const [caseIdFilterInput, setCaseIdFilterInput] = useState('');
  const caseIdFilterRef = useRef(caseIdFilter);
  caseIdFilterRef.current = caseIdFilter;

  const rows = useMemo(() => data.map((row) => ({ id: row.voucherId, ...row })), [data]);
  const reportingTagFilterOptions = useMemo(() => {
    const options = [...reportingTagOptions];
    selectedReportingTags.forEach((tag) => {
      if (!options.some((option) => option.value === tag)) {
        options.push({ value: tag, label: tag });
      }
    });
    return options;
  }, [reportingTagOptions, selectedReportingTags]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    voucherId: false,
    parentVoucherId: false,
    createdAt: false,
    updatedAt: false,
  });

  const handleDelete = useCallback((row: Voucher) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  }, []);

  const handleOpenWarnings = useCallback((row: Voucher) => {
    setWarningsDialogVoucher(row);
  }, []);

  const handleCloseWarnings = useCallback(() => {
    setWarningsDialogVoucher(undefined);
  }, []);

  const cancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setRowToDelete(undefined);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!rowToDelete) {
      return;
    }
    try {
      await onConfirmDelete(rowToDelete);
      cancelDelete();
    } catch (error) {
      // Keep dialog open if delete fails.
    }
  }, [cancelDelete, onConfirmDelete, rowToDelete]);

  const handleColumnVisibilityChange = useCallback((field: string, visible: boolean) => {
    setColumnVisibilityModel((prev) => ({ ...prev, [field]: visible }));
  }, []);

  const handleReportingTagsFilterChange = useCallback(
    (event: SelectChangeEvent<Array<string>>) => {
      const value = event.target.value;
      setSelectedReportingTags(typeof value === 'string' ? value.split(',') : value);
    },
    [setSelectedReportingTags],
  );

  const handleCaseIdFilterChange = useCallback(
    (_event: React.SyntheticEvent, newValue: ReadonlyArray<string>) => {
      const normalized = newValue
        .map((v) => normalizeCaseIdFilterToken(typeof v === 'string' ? v : ''))
        .filter(Boolean);
      setCaseIdFilter([...new Set(normalized)]);
      setCaseIdFilterInput('');
    },
    [setCaseIdFilter],
  );

  const flushPendingCaseIdFilterInput = useCallback(() => {
    setCaseIdFilterInput((input) => {
      const normalized = normalizeCaseIdFilterToken(input);
      if (!normalized) {
        return input;
      }
      const prev = caseIdFilterRef.current;
      setCaseIdFilter([...new Set([...prev, normalized])]);
      return '';
    });
  }, [setCaseIdFilter]);

  const columns = useMemo(
    () => getColumns({ onDelete: handleDelete, onOpenWarnings: handleOpenWarnings }),
    [handleDelete, handleOpenWarnings],
  );

  const warningsDialogList = warningsDialogVoucher ? getVoucherWarnings(warningsDialogVoucher) : [];

  const Toolbar = useCallback(
    () => (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1 }}>
        <ColumnSelector
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          size="small"
        />
      </Stack>
    ),
    [columnVisibilityModel, columns, handleColumnVisibilityChange],
  );

  return (
    <>
      <Grid container spacing={2} justifyContent="flex-end" alignItems="end">
        <Grid size={12} container spacing={2}>
          <Grid size={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <TextField
                label="Voucher Name"
                value={voucherName}
                onChange={(e) => setVoucherName(e.target.value)}
                fullWidth
                size="small"
                sx={{ flex: 1 }}
              />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel id="voucher-reporting-tags-filter-label">Reporting tags</InputLabel>
                <Select<Array<string>>
                  multiple
                  labelId="voucher-reporting-tags-filter-label"
                  value={selectedReportingTags}
                  onChange={handleReportingTagsFilterChange}
                  input={<OutlinedInput label="Reporting tags" />}
                  renderValue={(selected) =>
                    selected
                      .map((tag) => reportingTagFilterOptions.find((option) => option.value === tag)?.label ?? tag)
                      .join(', ')
                  }
                  fullWidth
                >
                  {reportingTagFilterOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <ListItemText primary={option.label} secondary={option.value} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Autocomplete
                multiple
                freeSolo
                options={[...CASE_ID_FILTER_PRESET_OPTIONS]}
                value={caseIdFilter}
                inputValue={caseIdFilterInput}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === 'reset') {
                    setCaseIdFilterInput('');
                    return;
                  }
                  setCaseIdFilterInput(newInputValue);
                }}
                onChange={handleCaseIdFilterChange}
                getOptionLabel={(option) => caseIdFilterOptionLabel(option)}
                isOptionEqualToValue={(a, b) => a === b}
                filterSelectedOptions
                fullWidth
                size="small"
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Case ID"
                    placeholder="set, not_set, or exact ID(s)"
                    inputProps={{
                      ...params.inputProps,
                      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                        params.inputProps?.onBlur?.(e);
                        const next = e.relatedTarget instanceof Node ? e.relatedTarget : null;
                        if (next?.closest?.('[role="listbox"]')) {
                          return;
                        }
                        flushPendingCaseIdFilterInput();
                      },
                    }}
                  />
                )}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={effectiveOnlyActive}
                    onChange={(_, checked) => setOnlyActive(checked)}
                    disabled={caseIdFilterActive}
                    color="primary"
                  />
                }
                label="Active vouchers only"
              />
            </Stack>
          </Grid>
        </Grid>
        <Grid size={4} container spacing={2}>
          <Stack direction="row" spacing={2} justifyContent="flex-end" width="100%">
            <Button variant="contained" color="primary" component={Link} to="/marketing/vouchers-create">
              New Voucher
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination, toolbar: Toolbar }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={rows}
          columns={columns}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.voucherId}
          paginationModel={{ page: pageNum, pageSize: pageSize }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
        />
      </Box>
      <Dialog open={deleteDialogOpen} onClose={cancelDelete}>
        <DialogTitle>Delete Voucher</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete voucher {rowToDelete?.voucherName || rowToDelete?.voucherId}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={cancelDelete} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleConfirmDelete} disabled={isDeleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(warningsDialogVoucher)} onClose={handleCloseWarnings} maxWidth="sm" fullWidth>
        <DialogTitle>
          Voucher warnings
          {warningsDialogVoucher ? ` — ${warningsDialogVoucher.voucherName}` : ''}
        </DialogTitle>
        <DialogContent>
          {warningsDialogList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No warnings for this voucher.
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {warningsDialogList.map((warning, index) => (
                <Alert
                  key={`${warning.code ?? 'warning'}-${index}`}
                  severity={getVoucherWarningSeverity(warning.level)}
                >
                  <AlertTitle>{warning.code ?? 'Voucher warning'}</AlertTitle>
                  {warning.message}
                </Alert>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCloseWarnings}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
